import React, { useState, useEffect } from 'react';
import {
  getSubadmins,
  createSubadmin,
  deleteSubadmin,
  updateSubadminDiscount,
  addSubadminBalance,
  getUsers,
  getActiveServices,
} from '../../lib/api';
import { formatDate, formatCurrency } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import {
  UserCog,
  Plus,
  Trash2,
  Loader2,
  Users,
  Percent,
  Wallet,
  StickyNote,
  KeyRound,
} from 'lucide-react';
import { ChangePasswordDialog } from '../../components/ChangePasswordDialog';
import UserNotesDialog from '../../components/UserNotesDialog';
import { toast } from 'sonner';

export default function SubadminsManagement() {
  const [subadmins, setSubadmins] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [balanceOpen, setBalanceOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesTarget, setNotesTarget] = useState(null);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [selectedSubadmin, setSelectedSubadmin] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [newSubadmin, setNewSubadmin] = useState({
    username: '',
    email: '',
    password: '',
    price_for_20: 20,
  });

  const [newPriceFor20, setNewPriceFor20] = useState(20);

  const [balanceForm, setBalanceForm] = useState({
    amount: '',
    reason: '',
  });

  const REFERENCE_PRICE = 20;
  const [servicesList, setServicesList] = useState([]);

  const priceToDiscount = (price) => ((REFERENCE_PRICE - price) / REFERENCE_PRICE) * 100;
  const discountToPrice = (discount) => REFERENCE_PRICE * (1 - discount / 100);
  const calcServicePrice = (basePrice, priceFor22) => basePrice * (priceFor22 / REFERENCE_PRICE);

  const fetchData = async () => {
    try {
      const [subadminsRes, usersRes, servicesRes] = await Promise.all([
        getSubadmins(),
        getUsers(),
        getActiveServices(),
      ]);
      setSubadmins(subadminsRes.data);
      setAllUsers(usersRes.data);
      setServicesList(servicesRes.data);
    } catch (err) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newSubadmin.username || !newSubadmin.email || !newSubadmin.password) {
      toast.error('Completa todos los campos');
      return;
    }
    if (newSubadmin.price_for_20 < 0) {
      toast.error('El precio no puede ser negativo');
      return;
    }
    setSubmitting(true);
    try {
      const discount_percentage = priceToDiscount(newSubadmin.price_for_20);
      await createSubadmin({
        username: newSubadmin.username,
        email: newSubadmin.email,
        password: newSubadmin.password,
        discount_percentage,
      });
      toast.success('Subadministrador creado');
      setCreateOpen(false);
      setNewSubadmin({ username: '', email: '', password: '', price_for_20: 20 });
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 
                      Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') :
                      'Error al crear subadministrador';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (subadmin) => {
    if (!window.confirm(`¿Eliminar subadministrador "${subadmin.username}"?`)) return;
    try {
      await deleteSubadmin(subadmin.id);
      toast.success('Subadministrador eliminado');
      fetchData();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const openDiscountDialog = (subadmin) => {
    setSelectedSubadmin(subadmin);
    setNewPriceFor20(parseFloat(discountToPrice(subadmin.discount_percentage).toFixed(2)));
    setDiscountOpen(true);
  };

  const openBalanceDialog = (subadmin) => {
    setSelectedSubadmin(subadmin);
    setBalanceForm({ amount: '', reason: '' });
    setBalanceOpen(true);
  };

  const handleAddBalance = async (e) => {
    e.preventDefault();
    if (!balanceForm.amount || parseFloat(balanceForm.amount) <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }
    setSubmitting(true);
    try {
      const res = await addSubadminBalance(selectedSubadmin.id, {
        amount: parseFloat(balanceForm.amount),
        reason: balanceForm.reason || 'Recarga de saldo',
      });
      toast.success(res.data.message);
      setBalanceOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Error al agregar saldo');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateDiscount = async () => {
    if (newPriceFor20 < 0) {
      toast.error('El precio no puede ser negativo');
      return;
    }

    const newDiscount = priceToDiscount(newPriceFor20);

    setSubmitting(true);
    try {
      await updateSubadminDiscount(selectedSubadmin.id, { discount_percentage: parseFloat(newDiscount.toFixed(2)) });
      toast.success(`Descuento actualizado a ${newDiscount.toFixed(2)}%`);
      setDiscountOpen(false);
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Error al actualizar descuento';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const getUsersBySubadmin = (subadminId) => {
    return allUsers.filter((u) => u.created_by === subadminId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subadmins-management">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Subadministradores</h1>
          <p className="text-muted-foreground mt-1">
            Gestiona los subadministradores y sus usuarios
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 btn-glow" data-testid="create-subadmin-btn">
              <Plus className="w-4 h-4" />
              Nuevo Subadmin
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Crear Subadministrador</DialogTitle>
              <DialogDescription>
                Los subadministradores pueden crear y gestionar sus propios usuarios
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Nombre de usuario</Label>
                <Input
                  id="username"
                  value={newSubadmin.username}
                  onChange={(e) =>
                    setNewSubadmin({ ...newSubadmin, username: e.target.value })
                  }
                  className="bg-secondary/50"
                  required
                  data-testid="subadmin-username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="text"
                  value={newSubadmin.email}
                  onChange={(e) =>
                    setNewSubadmin({ ...newSubadmin, email: e.target.value })
                  }
                  className="bg-secondary/50"
                  required
                  data-testid="subadmin-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={newSubadmin.password}
                  onChange={(e) =>
                    setNewSubadmin({ ...newSubadmin, password: e.target.value })
                  }
                  className="bg-secondary/50"
                  required
                  data-testid="subadmin-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price_for_20">Precio para Recarga de $20</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono">$</span>
                  <Input
                    id="price_for_20"
                    type="number"
                    min="0"
                    step="0.01"
                    value={newSubadmin.price_for_20}
                    onChange={(e) =>
                      setNewSubadmin({ ...newSubadmin, price_for_20: parseFloat(e.target.value) || 0 })
                    }
                    className="bg-secondary/50 pl-7"
                    data-testid="subadmin-price-for-20"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {newSubadmin.price_for_20 <= REFERENCE_PRICE ? (
                    <>Descuento calculado: <span className="text-emerald-400 font-bold">{priceToDiscount(newSubadmin.price_for_20).toFixed(2)}%</span></>
                  ) : (
                    <>Recargo calculado: <span className="text-orange-400 font-bold">+{Math.abs(priceToDiscount(newSubadmin.price_for_20)).toFixed(2)}%</span></>
                  )}
                </p>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCreateOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-subadmin"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Crear
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {subadmins.length === 0 ? (
        <Card className="bg-card border-border/40">
          <CardContent className="text-center py-12 text-muted-foreground">
            <UserCog className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No hay subadministradores</p>
            <p className="text-sm">Crea el primer subadministrador</p>
          </CardContent>
        </Card>
      ) : (
        <Accordion type="single" collapsible className="space-y-4">
          {subadmins.map((subadmin) => {
            const subadminUsers = getUsersBySubadmin(subadmin.id);
            return (
              <AccordionItem
                key={subadmin.id}
                value={subadmin.id}
                className="bg-card border border-border/40 rounded-lg overflow-hidden"
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-secondary/30">
                  <div className="flex items-center gap-4 w-full">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <UserCog className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-bold">{subadmin.username}</p>
                      <p className="text-sm text-muted-foreground">{subadmin.email}</p>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-4 mr-4">
                      <Badge variant="outline" className="gap-1 bg-accent/10 text-accent border-accent/30">
                        <Wallet className="w-3 h-3" />
                        {formatCurrency(subadmin.balance)}
                      </Badge>
                      <Badge variant="outline" className="gap-1 bg-primary/10 text-primary border-primary/30">
                        <Percent className="w-3 h-3" />
                        {subadmin.discount_percentage}%
                      </Badge>
                      <Badge variant="secondary" className="gap-1">
                        <Users className="w-3 h-3" />
                        {subadminUsers.length} usuarios
                      </Badge>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <h4 className="font-medium text-sm text-muted-foreground">
                        Usuarios de {subadmin.username}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openBalanceDialog(subadmin)}
                          data-testid={`add-balance-${subadmin.username}`}
                        >
                          <Wallet className="w-4 h-4 mr-1" />
                          Agregar Saldo
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDiscountDialog(subadmin)}
                          data-testid={`adjust-discount-${subadmin.username}`}
                        >
                          <Percent className="w-4 h-4 mr-1" />
                          Ajustar Descuento
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setNotesTarget(subadmin);
                            setPasswordOpen(true);
                          }}
                          data-testid={`change-password-subadmin-${subadmin.username}`}
                        >
                          <KeyRound className="w-4 h-4 mr-1" />
                          Cambiar Contraseña
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setNotesTarget(subadmin);
                            setNotesOpen(true);
                          }}
                          data-testid={`notes-subadmin-${subadmin.username}`}
                        >
                          <StickyNote className="w-4 h-4 mr-1" />
                          Notas
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(subadmin)}
                          data-testid={`delete-subadmin-${subadmin.username}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Eliminar
                        </Button>
                      </div>
                    </div>

                    {subadminUsers.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Este subadministrador no tiene usuarios
                      </p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow className="border-border/50">
                            <TableHead>Usuario</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Saldo</TableHead>
                            <TableHead>Descuento</TableHead>
                            <TableHead>Estado</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {subadminUsers.map((user) => (
                            <TableRow
                              key={user.id}
                              className="border-border/30"
                            >
                              <TableCell className="font-medium">
                                {user.username}
                              </TableCell>
                              <TableCell className="text-muted-foreground">
                                {user.email}
                              </TableCell>
                              <TableCell className="font-mono text-accent">
                                ${user.balance.toFixed(2)}
                              </TableCell>
                              <TableCell className="font-mono text-primary">
                                {user.discount_percentage}%
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant="outline"
                                  className={
                                    user.is_active
                                      ? 'status-active'
                                      : 'status-inactive'
                                  }
                                >
                                  {user.is_active ? 'Activo' : 'Inactivo'}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      {/* Adjust Discount Dialog */}
      <Dialog open={discountOpen} onOpenChange={setDiscountOpen}>
        <DialogContent className="bg-card border-border sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Ajustar Descuento</DialogTitle>
            <DialogDescription>
              Descuento actual de {selectedSubadmin?.username}: <span className="text-primary font-bold">{selectedSubadmin?.discount_percentage}%</span>
              {' '}(paga <span className="text-accent font-bold">${discountToPrice(selectedSubadmin?.discount_percentage || 0).toFixed(2)}</span> por la de $20)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Precio que pagará por la Recarga de $20</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-lg">$</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newPriceFor20}
                  onChange={(e) => setNewPriceFor20(parseFloat(e.target.value) || 0)}
                  className="bg-secondary/50 text-lg pl-7 font-mono"
                  data-testid="discount-price-input"
                />
              </div>
            </div>

            <Card className="bg-secondary/30 border-border/30">
              <CardContent className="p-4 space-y-2">
                <div className="flex justify-between items-center pb-2 border-b border-border/30">
                  <span className="text-muted-foreground">
                    {newPriceFor20 <= REFERENCE_PRICE ? 'Descuento calculado:' : 'Recargo calculado:'}
                  </span>
                  <span className={`text-xl font-bold ${newPriceFor20 <= REFERENCE_PRICE ? 'text-emerald-400' : 'text-orange-400'}`}>
                    {newPriceFor20 <= REFERENCE_PRICE
                      ? `${priceToDiscount(newPriceFor20).toFixed(2)}%`
                      : `+${Math.abs(priceToDiscount(newPriceFor20)).toFixed(2)}%`}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground pt-1 pb-2">Precios para todas las recargas:</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm max-h-48 overflow-y-auto">
                  {servicesList.map((svc) => (
                    <div key={svc.id} className="flex justify-between py-1">
                      <span className="text-muted-foreground">{svc.cup_amount} CUP:</span>
                      <span className="font-mono">${calcServicePrice(svc.base_price, newPriceFor20).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDiscountOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpdateDiscount}
              disabled={submitting || newPriceFor20 < 0}
              data-testid="submit-discount-adjust"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Balance Dialog */}
      <Dialog open={balanceOpen} onOpenChange={setBalanceOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Agregar Saldo</DialogTitle>
            <DialogDescription>
              Saldo actual de {selectedSubadmin?.username}: <span className="text-accent font-bold">{formatCurrency(selectedSubadmin?.balance || 0)}</span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddBalance} className="space-y-4">
            <div className="space-y-2">
              <Label>Monto ($)</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={balanceForm.amount}
                onChange={(e) => setBalanceForm({ ...balanceForm, amount: e.target.value })}
                className="bg-secondary/50 text-lg"
                placeholder="0.00"
                required
                data-testid="balance-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Razón (opcional)</Label>
              <Input
                value={balanceForm.reason}
                onChange={(e) => setBalanceForm({ ...balanceForm, reason: e.target.value })}
                className="bg-secondary/50"
                placeholder="Ej: Pago recibido"
                data-testid="balance-reason-input"
              />
            </div>
            <Card className="bg-secondary/30 border-border/30">
              <CardContent className="p-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Nuevo saldo:</span>
                  <span className="text-2xl font-bold text-accent">
                    {formatCurrency((selectedSubadmin?.balance || 0) + (parseFloat(balanceForm.amount) || 0))}
                  </span>
                </div>
              </CardContent>
            </Card>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setBalanceOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="submit-balance"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Agregar Saldo
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} targetUser={notesTarget} />
      {/* Subadmin Notes Dialog */}
      <UserNotesDialog
        open={notesOpen}
        onOpenChange={setNotesOpen}
        targetUser={notesTarget}
      />
    </div>
  );
}
